// Manages active bullets: integration, collision against planes, lifecycle.

import * as THREE from 'three';
import { Bullet } from './Bullet';
import type { Plane } from './Plane';

export interface HitEvent {
  victim: Plane;
  killer?: Plane;
  bullet: Bullet;
  killed: boolean;
}

const HIT_RADIUS = 1.2;

export class BulletSystem {
  bullets: Bullet[] = [];
  scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  spawn(b: Bullet): void {
    this.bullets.push(b);
    this.scene.add(b.mesh);
  }

  update(dt: number, planes: Plane[], now: number, planeById: Map<string, Plane>, onHit: (e: HitEvent) => void): void {
    for (const b of this.bullets) {
      if (!b.alive) continue;
      b.update(dt);
      if (!b.alive) continue;
      // Broad phase: linear scan since plane count is small (<= 8).
      for (const p of planes) {
        if (!p.alive) continue;
        if (p.callsign === b.ownerId) continue;
        if (p.team === b.team) continue;
        const dx = p.position.x - b.position.x;
        const dy = p.position.y - b.position.y;
        const dz = p.position.z - b.position.z;
        if (dx * dx + dy * dy + dz * dz < HIT_RADIUS * HIT_RADIUS) {
          const killed = p.takeDamage(b.damage, now);
          const killer = planeById.get(b.ownerId);
          onHit({ victim: p, killer, bullet: b, killed });
          b.alive = false;
          break;
        }
      }
    }
    // Sweep dead bullets.
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      if (!this.bullets[i].alive) {
        this.scene.remove(this.bullets[i].mesh);
        this.bullets.splice(i, 1);
      }
    }
  }

  clear(): void {
    for (const b of this.bullets) this.scene.remove(b.mesh);
    this.bullets.length = 0;
  }
}
